import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { IndexaContribution, IndexaPlanSummary } from '../../api/thirdparty/indexa/types';
import getIndexaPlansSummary from '../../api/thirdparty/indexa/getIndexaPlansSummary';
import getIndexaContributionYears from '../../api/thirdparty/indexa/getIndexaContributionYears';
import getIndexaContributions from '../../api/thirdparty/indexa/getIndexaContributions';
import IndexaContributionsListV2 from './IndexaContributionsListV2';

interface IndexaPlanSummaryV2Props {
	token: string;
	translate: (key: string) => string;
}

function formatCurrency(amount: number, currency: string = 'EUR'): string {
	return new Intl.NumberFormat('es-ES', {
		style: 'currency',
		currency
	}).format(amount);
}

function formatPercentage(value: number): string {
	const sign = value > 0 ? '+' : '';
	return `${sign}${value.toFixed(2).replace('.', ',')}%`;
}

const IndexaPlanSummaryV2: React.FC<IndexaPlanSummaryV2Props> = ({ token, translate }) => {
	const [plans, setPlans] = useState<IndexaPlanSummary[]>([]);
	const [years, setYears] = useState<number[]>([]);
	const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
	const [contributions, setContributions] = useState<IndexaContribution[]>([]);

	const [isLoadingInitial, setIsLoadingInitial] = useState(true);
	const [isLoadingContributions, setIsLoadingContributions] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [contributionsError, setContributionsError] = useState<string | null>(null);

	const currentYear = useMemo(() => new Date().getFullYear(), []);

	const yearsForSelector = useMemo(() => {
		const set = new Set<number>(years);
		set.add(currentYear);
		return Array.from(set).sort((a, b) => b - a);
	}, [years, currentYear]);

	const historicTotalContributed = useMemo(
		() => plans.reduce((sum, plan) => sum + (plan.totalContributed || 0), 0),
		[plans]
	);

	const fetchInitial = useCallback(async () => {
		setIsLoadingInitial(true);
		setError(null);
		try {
			const [plansResponse, yearsResponse] = await Promise.all([
				getIndexaPlansSummary(token),
				getIndexaContributionYears(token)
			]);
			const fetchedPlans = plansResponse.data?.plans ?? [];
			const fetchedYears = yearsResponse.data?.years ?? [];

			setPlans(fetchedPlans);
			setYears(fetchedYears);

			const defaultYear = fetchedYears.length > 0 ? fetchedYears[0] : currentYear;
			setSelectedYear(defaultYear);
		} catch (err) {
			setError(translate('indexaCapital.plans.error'));
		} finally {
			setIsLoadingInitial(false);
		}
	}, [token, translate, currentYear]);

	const fetchContributionsFor = useCallback(
		async (year: number) => {
			setIsLoadingContributions(true);
			setContributionsError(null);
			try {
				const response = await getIndexaContributions(token, year);
				setContributions(response.data ?? []);
			} catch (err) {
				setContributionsError(translate('indexaCapital.contributions.error'));
				setContributions([]);
			} finally {
				setIsLoadingContributions(false);
			}
		},
		[token, translate]
	);

	useEffect(() => {
		fetchInitial();
	}, [fetchInitial]);

	useEffect(() => {
		if (selectedYear !== undefined) {
			fetchContributionsFor(selectedYear);
		}
	}, [selectedYear, fetchContributionsFor]);

	if (isLoadingInitial) {
		return (
			<div className="flex flex-col items-center justify-center gap-4 py-8">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
				<p className="text-sm text-muted-foreground">{translate('indexaCapital.plans.loading')}</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
				<p className="text-sm text-destructive">{error}</p>
			</div>
		);
	}

	if (plans.length === 0) {
		return (
			<div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3">
				<p className="text-sm text-muted-foreground">{translate('indexaCapital.plans.noPlans')}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="grid gap-4 md:grid-cols-1">
				{plans.map((plan) => {
					const isProfitPositive = plan.profit >= 0;
					return (
						<div key={plan.accountNumber} className="rounded-lg border border-border bg-card p-4">
							<div className="flex items-start justify-between gap-3 mb-4">
								<div>
									<p className="text-base font-semibold text-foreground">{plan.name}</p>
									{plan.providerName && (
										<p className="text-xs text-muted-foreground mt-0.5">
											{plan.providerName}
											{plan.fundedAt && ` · ${plan.fundedAt}`}
										</p>
									)}
								</div>
							</div>

							<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
								<div className="rounded-md bg-muted/40 p-3">
									<div className="flex items-center gap-2 mb-1">
										<Wallet className="w-4 h-4 text-muted-foreground" />
										<p className="text-xs text-muted-foreground">
											{translate('indexaCapital.plans.currentBalance')}
										</p>
									</div>
									<p className="text-lg font-bold text-foreground">
										{formatCurrency(plan.currentBalance, plan.currency)}
									</p>
								</div>

								<div className="rounded-md bg-muted/40 p-3">
									<p className="text-xs text-muted-foreground mb-1">
										{translate('indexaCapital.plans.totalContributed')}
									</p>
									<p className="text-lg font-bold text-foreground">
										{formatCurrency(plan.totalContributed, plan.currency)}
									</p>
								</div>

								<div
									className={`rounded-md p-3 ${
										isProfitPositive ? 'bg-primary/5' : 'bg-destructive/5'
									}`}
								>
									<div className="flex items-center gap-2 mb-1">
										{isProfitPositive ? (
											<TrendingUp className="w-4 h-4 text-primary" />
										) : (
											<TrendingDown className="w-4 h-4 text-destructive" />
										)}
										<p className="text-xs text-muted-foreground">
											{translate('indexaCapital.plans.profit')}
										</p>
									</div>
									<p
										className={`text-lg font-bold ${
											isProfitPositive ? 'text-primary' : 'text-destructive'
										}`}
									>
										{formatCurrency(plan.profit, plan.currency)}{' '}
										<span className="text-sm font-medium">
											({formatPercentage(plan.profitPercentage)})
										</span>
									</p>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			<div className="flex items-center justify-between gap-4 flex-wrap">
				<p className="text-sm text-muted-foreground">
					{translate('indexaCapital.plans.historicTotal')}{' '}
					<span className="font-semibold text-foreground">
						{formatCurrency(historicTotalContributed)}
					</span>
				</p>

				<div className="flex items-center gap-2">
					<p className="text-sm text-muted-foreground">{translate('indexaCapital.plans.year')}</p>
					<Select
						value={selectedYear !== undefined ? String(selectedYear) : ''}
						onValueChange={(value) => setSelectedYear(Number(value))}
					>
						<SelectTrigger className="w-[120px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{yearsForSelector.map((year) => (
								<SelectItem key={year} value={String(year)}>
									{year}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<IndexaContributionsListV2
				contributions={contributions}
				isLoading={isLoadingContributions}
				error={contributionsError || undefined}
				translate={translate}
			/>
		</div>
	);
};

export default IndexaPlanSummaryV2;
